import { Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import GrinAiAvatar from 'components/common/icons/GrinAiAvatar'
import ProfilePicture from 'components/common/ProfilePicture'
import Tooltip from 'components/common/Tooltip'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import usePracticeTeamMembers from 'hooks/usePracticeTeamMembers'
import moment from 'moment'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createIdForTest } from 'utils/testingUtils.js'
import useNoteVisibilityOptions from '../useNoteVisibilityOptions'
import NoteItemVisiblityMenu from './NoteItemVisiblityMenu'

const useStyles = makeStyles(theme => ({
  circle: {
    margin: '0 6px',
    fontSize: 5,
    color: 'rgba(0, 0, 0, 0.3)',
    verticalAlign: 'middle'
  },
  softLabel: {
    color: 'var(--text-color-11)'
  },
  visibilitySpacing: {
    marginLeft: 4
  }
}))

const CreatorInfo = ({
  noteId,
  userId,
  noteParsedMetadata,
  createdAt,
  visibility,
  onVisibilitySelected = () => {},
  isVisibilityEditable
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const { practiceTeamMembers } = usePracticeTeamMembers()

  const currentMember = useMemo(
    () => practiceTeamMembers?.find(member => member?.grinUserId === userId),
    [practiceTeamMembers, userId]
  )

  const { creatorName: fallbackCreatorName, creatorRoleDescription: fallbackCreatorRoleDescription } =
    noteParsedMetadata

  const name = useMemo(() => currentMember?.name || fallbackCreatorName, [currentMember, fallbackCreatorName])
  const sentByGrin = useMemo(() => !userId && name === 'Grin', [userId, name])

  const roleDescription = useMemo(
    () => currentMember?.doctor?.roleDescription || fallbackCreatorRoleDescription,
    [currentMember, fallbackCreatorRoleDescription]
  )

  const { currentVisibilityLabel, hasAnyVisibilityOption, visibilityOptions } = useNoteVisibilityOptions({
    currentNoteVisibility: visibility
  })

  return (
    <Grid container alignItems="center" wrap="nowrap" spacing={1}>
      <Grid item>
        {sentByGrin ? (
          <GrinAiAvatar />
        ) : (
          <ProfilePicture size="small" photo={currentMember?.photo} name={name} className={classes.profilePicture} />
        )}
      </Grid>
      <Grid item>
        <Grid container direction="column">
          <Grid item>
            <DazzedParagraph12>
              <strong>{name || t('pages.patients.patientSwitchableView.notes.noteBody.unknownMember')}</strong>
              {roleDescription ? (
                <>
                  <span className={classes.circle}>{`●`}</span>
                  <span style={{ fontWeight: 400 }}>{roleDescription}</span>
                </>
              ) : (
                ''
              )}
            </DazzedParagraph12>
          </Grid>
          <Grid item>
            <Grid container>
              <Grid item>
                <DazzedParagraph12 className={classes.softLabel}>{`${moment(createdAt).format(
                  'MMM DD, YYYY'
                )} (${moment(createdAt).fromNow()})`}</DazzedParagraph12>
              </Grid>
              {noteParsedMetadata.lastEditedAt && (
                <Grid item>
                  <Tooltip
                    placement="bottom"
                    value={t('pages.patients.patientSwitchableView.notes.lastEditedAtTooltip', {
                      date: moment(noteParsedMetadata.lastEditedAt).format('LLL')
                    })}
                  >
                    <DazzedParagraph12
                      id={createIdForTest({ componentId: 'patient-note', title: noteId, elementId: 'edited' })}
                      className={classes.softLabel}
                    >
                      <span className={classes.circle}>{`●`}</span>
                      {t('pages.patients.patientSwitchableView.notes.edited')}
                    </DazzedParagraph12>
                  </Tooltip>
                </Grid>
              )}
              {hasAnyVisibilityOption && isVisibilityEditable && (
                <>
                  <Grid item className={classes.visibilitySpacing}>
                    |
                  </Grid>
                  <Grid item className={classes.visibilitySpacing}>
                    <NoteItemVisiblityMenu
                      id={createIdForTest({ componentId: 'patient-note', title: noteId, elementId: 'visibility' })}
                      currentVisibilityLabel={currentVisibilityLabel}
                      visibilityOptions={visibilityOptions}
                      triggerLabelClassName={classes.softLabel}
                      onVisibilitySelected={onVisibilitySelected}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default CreatorInfo
